/**
 * Usage Tracking and Plan Management Utilities
 * 
 * This module defines subscription tiers, add-ons, and utility functions
 * for tracking usage and enforcing limits.
 * 
 * Tiers:
 * - starter: $99/mo, 3 agents, 25 evaluations
 * - pro: $199/mo, 10 agents, 100 evaluations
 * - pro_plus: $399/mo, 25 agents, 300 evaluations, white labeling included
 * - platinum: custom pricing, unlimited agents, custom evaluations
 *
 * Special statuses:
 * - 'lifetime': Grandfathered teams with no billing (Business features forever)
 */

// Plan definitions with limits and pricing
export const PLANS = {
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small teams getting started',
    monthlyLimit: 25,
    agentLimit: 3,
    monthlyCost: 99,
    yearlyCost: 1089, // 1 month free
    stripePriceIdMonthly: null, // Set after Stripe setup
    stripePriceIdYearly: null,
    features: [
      'Team lead + 3 agents',
      '25 call evaluations/month',
      'Full script builder',
      'Team management',
      'Basic analytics',
      'Email support'
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For growing teams that need more capacity',
    monthlyLimit: 100,
    agentLimit: 10,
    monthlyCost: 199,
    yearlyCost: 2189, // 1 month free
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,
    popular: true, // Mark as most popular
    features: [
      'Team lead + 10 agents',
      '100 call evaluations/month',
      'Full script builder',
      'Team management',
      'Advanced analytics',
      'Priority support'
    ]
  },
  pro_plus: {
    id: 'pro_plus',
    name: 'Business',
    description: 'For established teams running serious operations',
    monthlyLimit: 300,
    agentLimit: 25,
    monthlyCost: 399,
    yearlyCost: 4389, // 1 month free
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,
    whiteLabelIncluded: true,
    features: [
      'Everything in Pro, plus:',
      'Team lead + 25 agents',
      '300 call evaluations/month',
      'Per-script lead capture forms',
      'White label branding included',
      'Dedicated support'
    ]
  },
  platinum: {
    id: 'platinum',
    name: 'Platinum',
    description: 'Custom setup for large brokerages and investment operations',
    monthlyLimit: null,
    agentLimit: null, // Unlimited
    monthlyCost: null,
    isCustom: true,
    stripePriceIdMonthly: null,
    stripePriceIdYearly: null,
    features: [
      'Everything in Business, plus:',
      '25+ agents (custom)',
      'Custom evaluation volume',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee',
      'Annual contracts available'
    ]
  }
};

// Add-on packages for a la carte purchases
export const ADDONS = {
  white_label: {
    id: 'white_label',
    name: 'White Labeling',
    description: 'Custom branding for your team',
    monthlyCost: 99,
    stripePriceId: null,
    type: 'recurring'
  },
  extra_agent: {
    id: 'extra_agent',
    name: 'Additional Agent',
    description: 'Add one more agent to your team',
    monthlyCost: 20,
    stripePriceId: null,
    type: 'recurring'
  },
  eval_pack_10: {
    id: 'eval_pack_10',
    name: '10 Evaluation Credits',
    description: '10 additional call evaluations',
    price: 10,
    credits: 10,
    pricePerCredit: 1.00,
    stripePriceId: null,
    type: 'one_time'
  },
  eval_pack_20: {
    id: 'eval_pack_20',
    name: '20 Evaluation Credits',
    description: '20 additional call evaluations',
    price: 18,
    credits: 20,
    pricePerCredit: 0.90,
    stripePriceId: null,
    type: 'one_time',
    savings: '10%'
  },
  eval_pack_50: {
    id: 'eval_pack_50',
    name: '50 Evaluation Credits',
    description: '50 additional call evaluations',
    price: 40,
    credits: 50,
    pricePerCredit: 0.80,
    stripePriceId: null,
    type: 'one_time',
    savings: '20%',
    popular: true
  },
  eval_pack_100: {
    id: 'eval_pack_100',
    name: '100 Evaluation Credits',
    description: '100 additional call evaluations',
    price: 75,
    credits: 100,
    pricePerCredit: 0.75,
    stripePriceId: null,
    type: 'one_time',
    savings: '25%'
  }
};

// Subscription statuses
export const SUBSCRIPTION_STATUSES = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  LIFETIME: 'lifetime' // Special status for grandfathered teams
};

/**
 * Get current billing period in format YYYY-MM
 */
export function getCurrentPeriod() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Check if usage data needs to be reset (new month)
 */
export function shouldResetUsage(currentPeriod, lastPeriod) {
  return currentPeriod !== lastPeriod;
}

/**
 * Calculate remaining trial days
 */
export function getTrialDaysRemaining(trialEndsAt) {
  if (!trialEndsAt) return 0;
  
  const endDate = trialEndsAt.toDate ? trialEndsAt.toDate() : new Date(trialEndsAt);
  const now = new Date();
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
}

/**
 * Check if trial has expired
 */
export function isTrialExpired(trialEndsAt) {
  if (!trialEndsAt) return false;
  
  const endDate = trialEndsAt.toDate ? trialEndsAt.toDate() : new Date(trialEndsAt);
  return new Date() > endDate;
}

/**
 * Get the effective evaluation limit including bonus credits
 */
export function getEffectiveEvalLimit(subscription) {
  if (!subscription) return 0;
  
  const planConfig = PLANS[subscription.plan] || PLANS.starter;
  const baseLimit = subscription.evalLimit || planConfig.monthlyLimit;
  const bonusCredits = subscription.addons?.bonusEvalCredits || 0;
  
  return baseLimit + bonusCredits;
}

/**
 * Get the effective agent limit including extra agents
 */
export function getEffectiveAgentLimit(subscription) {
  if (!subscription) return 0;
  
  // Lifetime or Platinum = unlimited
  if (subscription.status === SUBSCRIPTION_STATUSES.LIFETIME ||
      subscription.plan === 'platinum') {
    return null; // null = unlimited
  }
  
  const planConfig = PLANS[subscription.plan] || PLANS.starter;
  const baseLimit = subscription.agentLimit ?? planConfig.agentLimit;
  const extraAgents = subscription.addons?.extraAgents || 0;

  // Trial has no agents
  if (subscription.status === SUBSCRIPTION_STATUSES.TRIALING) {
    return 0;
  }

  // null base limit means unlimited (e.g. pro_plus / Business plan)
  if (baseLimit === null) return null;

  return baseLimit + extraAgents;
}

/**
 * Check if white labeling is enabled for subscription
 */
export function isWhiteLabelEnabled(subscription) {
  if (!subscription) return false;
  
  // Lifetime users always have white labeling
  if (subscription.status === SUBSCRIPTION_STATUSES.LIFETIME) {
    return true;
  }
  
  // Business and Platinum include white labeling
  if (subscription.plan === 'pro_plus' || subscription.plan === 'platinum') {
    return true;
  }
  
  // Check add-on
  return subscription.addons?.whiteLabelEnabled || false;
}

/**
 * Get usage status for UI display
 */
export function getUsageStatus(plan, evaluationsUsed, bonusCredits = 0, status = 'active') {
  const planConfig = PLANS[plan] || PLANS.starter;
  const isLifetime = status === SUBSCRIPTION_STATUSES.LIFETIME;
  
  // Lifetime plans have their limits but no billing
  const totalLimit = planConfig.monthlyLimit + bonusCredits;
  const remaining = Math.max(0, totalLimit - evaluationsUsed);
  const isOverLimit = evaluationsUsed >= totalLimit;
  const percentUsed = Math.min(100, (evaluationsUsed / totalLimit) * 100);
  
  let statusText = 'available';
  let message = `${remaining} evaluations remaining this month`;
  let warningLevel = 'none'; // none, warning, critical, blocked
  
  if (isOverLimit) {
    statusText = 'blocked';
    message = 'Monthly limit reached. Purchase more credits or upgrade your plan.';
    warningLevel = 'blocked';
  } else if (remaining <= 2) {
    warningLevel = 'critical';
    message = `Only ${remaining} evaluation${remaining !== 1 ? 's' : ''} remaining`;
  } else if (remaining <= 5) {
    warningLevel = 'warning';
    message = `${remaining} evaluations remaining - consider purchasing more`;
  }
  
  return {
    status: statusText,
    remaining,
    used: evaluationsUsed,
    limit: totalLimit,
    isOverLimit,
    percentUsed,
    message,
    warningLevel,
    canUpload: statusText !== 'blocked',
    isLifetime
  };
}

/**
 * Get agent usage status for UI display
 */
export function getAgentUsageStatus(subscription, currentAgentCount) {
  if (!subscription) {
    return { canAddAgent: false, message: 'No subscription found' };
  }
  
  const limit = getEffectiveAgentLimit(subscription);
  
  // Unlimited agents
  if (limit === null) {
    return {
      canAddAgent: true,
      current: currentAgentCount,
      limit: null,
      remaining: null,
      message: 'Unlimited agents',
      isUnlimited: true
    };
  }
  
  // Trial - no agents allowed
  if (subscription.status === SUBSCRIPTION_STATUSES.TRIALING) {
    return {
      canAddAgent: false,
      current: currentAgentCount,
      limit: 0,
      remaining: 0,
      message: 'Upgrade to add team members',
      isTrialing: true
    };
  }
  
  const remaining = Math.max(0, limit - currentAgentCount);
  const canAddAgent = remaining > 0;
  
  return {
    canAddAgent,
    current: currentAgentCount,
    limit,
    remaining,
    percentUsed: Math.min(100, (currentAgentCount / limit) * 100),
    message: canAddAgent 
      ? `${remaining} agent slot${remaining !== 1 ? 's' : ''} available`
      : 'Agent limit reached. Purchase additional agents or upgrade.',
    isUnlimited: false
  };
}

/**
 * Get initial usage data structure for a new team
 */
export function getInitialUsageData(plan = 'starter') {
  const planConfig = PLANS[plan] || PLANS.starter;
  return {
    currentPeriod: getCurrentPeriod(),
    evaluationsUsed: 0,
    lastReset: new Date()
  };
}

/**
 * Get initial subscription data for a new team
 */
export function getInitialSubscription(plan = 'starter', billingInterval = 'month') {
  const planConfig = PLANS[plan] || PLANS.starter;
  const now = new Date();
  
  return {
    plan: plan,
    planName: planConfig.name,
    status: SUBSCRIPTION_STATUSES.ACTIVE,
    billingInterval: billingInterval,
    
    // Stripe (populated after checkout)
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    
    // Limits
    evalLimit: planConfig.monthlyLimit,
    agentLimit: planConfig.agentLimit,
    
    // Billing period
    currentPeriodStart: now,
    currentPeriodEnd: getNextBillingDate(now, billingInterval),
    
    // Add-ons
    addons: {
      whiteLabelEnabled: planConfig.whiteLabelIncluded || false,
      extraAgents: 0,
      bonusEvalCredits: 0
    },
    
    // Usage tracking
    usage: {
      evaluationsUsed: 0,
      agentCount: 1, // Team lead counts as 1
      lastReset: now
    },
    
    // Metadata
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Get initial subscription for lifetime (grandfathered) teams
 */
export function getLifetimeSubscription() {
  const now = new Date();
  
  return {
    plan: 'pro_plus',
    planName: 'Business (Lifetime)',
    status: SUBSCRIPTION_STATUSES.LIFETIME,
    billingInterval: null,
    
    // No Stripe for lifetime
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    
    // Business limits
    evalLimit: PLANS.pro_plus.monthlyLimit,
    agentLimit: null, // Unlimited
    
    // No trial/expiration
    trialEndsAt: null,
    currentPeriodStart: now,
    currentPeriodEnd: null, // Never expires
    
    // Full add-ons included
    addons: {
      whiteLabelEnabled: true,
      extraAgents: 0,
      bonusEvalCredits: 0
    },
    
    // Usage tracking
    usage: {
      evaluationsUsed: 0,
      agentCount: 1,
      lastReset: now
    },
    
    // Metadata
    grantedAt: now,
    grantedReason: 'early_adopter_lifetime',
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Calculate next billing date
 */
function getNextBillingDate(fromDate, interval) {
  const date = new Date(fromDate);
  if (interval === 'year') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }
  return date;
}

/**
 * Format cost for display
 */
export function formatCost(amount) {
  return `$${amount.toFixed(2)}`;
}

/**
 * Get color class based on warning level
 */
export function getWarningColor(warningLevel) {
  const colors = {
    none: 'green',
    warning: 'yellow',
    critical: 'orange',
    blocked: 'red'
  };
  return colors[warningLevel] || 'gray';
}

/**
 * Get plan comparison data for pricing display
 */
export function getPlanComparison() {
  return Object.values(PLANS).map(plan => ({
    ...plan,
    monthlyPriceDisplay: formatCost(plan.monthlyCost),
    yearlyPriceDisplay: formatCost(plan.yearlyCost),
    yearlyMonthlyCost: plan.yearlyCost / 12,
    yearlySavings: (plan.monthlyCost * 12) - plan.yearlyCost,
    yearlySavingsPercent: Math.round(((plan.monthlyCost * 12 - plan.yearlyCost) / (plan.monthlyCost * 12)) * 100)
  }));
}

/**
 * Check if a subscription is in good standing (can use the app)
 */
export function isSubscriptionActive(subscription) {
  if (!subscription) return false;
  
  const activeStatuses = [
    SUBSCRIPTION_STATUSES.ACTIVE,
    SUBSCRIPTION_STATUSES.LIFETIME
  ];
  
  // Trialing is active if not expired
  if (subscription.status === SUBSCRIPTION_STATUSES.TRIALING) {
    return !isTrialExpired(subscription.trialEndsAt);
  }
  
  return activeStatuses.includes(subscription.status);
}

/**
 * Get subscription status display info
 */
export function getSubscriptionStatusDisplay(subscription) {
  if (!subscription) {
    return { label: 'No Subscription', color: 'gray', icon: '⚠️' };
  }
  
  switch (subscription.status) {
    case SUBSCRIPTION_STATUSES.LIFETIME:
      return { label: 'Lifetime License', color: 'purple', icon: '👑' };
    case SUBSCRIPTION_STATUSES.ACTIVE:
      return { label: 'Active', color: 'green', icon: '✅' };
    case SUBSCRIPTION_STATUSES.TRIALING: {
      const daysLeft = getTrialDaysRemaining(subscription.trialEndsAt);
      if (daysLeft <= 0) {
        return { label: 'Trial Expired', color: 'red', icon: '⏰' };
      }
      return { label: `Trial (${daysLeft} day${daysLeft !== 1 ? 's' : ''} left)`, color: 'blue', icon: '🎁' };
    }
    case SUBSCRIPTION_STATUSES.PAST_DUE:
      return { label: 'Payment Failed', color: 'red', icon: '⚠️' };
    case SUBSCRIPTION_STATUSES.CANCELED:
      return { label: 'Canceled', color: 'gray', icon: '❌' };
    default:
      return { label: 'Unknown', color: 'gray', icon: '❓' };
  }
}
